/**
 * VirtualizedTable Component
 * 
 * A performant table component with virtualized rows for handling
 * 1000+ items. Provides a Jira-like dense table view with:
 * - Sortable column headers
 * - Resizable columns
 * - Row selection with shift-click range select
 * - Keyboard navigation (j/k, Enter)
 * - Sticky header
 */

import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import type { ReactNode } from 'react';
import type { ItemRenderProps, TableColumn, SortState } from './types';

// ============================================================================
// Types
// ============================================================================

export interface VirtualizedTableColumn<T> extends TableColumn {
  /** Render cell content */
  render: (item: T, props: ItemRenderProps) => ReactNode;
  
  /** Minimum width */
  minWidth?: number;
  
  /** Maximum width */
  maxWidth?: number;
  
  /** Whether column is resizable */
  resizable?: boolean;
}

export interface VirtualizedTableProps<T, SortField = string> {
  /** Items to render */
  items: T[];
  
  /** Extract unique ID from item */
  getId: (item: T) => string;
  
  /** Column definitions */
  columns: VirtualizedTableColumn<T>[];
  
  /** Row height */
  rowHeight?: number;
  
  /** Header height */
  headerHeight?: number;
  
  /** Selected item IDs */
  selectedIds: Set<string>;
  
  /** Toggle item selection */
  onToggle: (id: string) => void;
  
  /** Select range (shift-click) */
  onSelectRange?: (fromId: string, toId: string) => void;
  
  /** Select all */
  onSelectAll?: () => void;
  
  /** Deselect all */
  onDeselectAll?: () => void;
  
  /** Click handler for row */
  onRowClick?: (item: T) => void;
  
  /** Double-click handler */
  onRowDoubleClick?: (item: T) => void;
  
  /** Whether selection is enabled */
  selectable?: boolean;
  
  /** Current sort state */
  currentSort?: SortState<SortField>;
  
  /** Sort change handler */
  onSortChange?: (field: SortField, direction?: 'asc' | 'desc') => void;
  
  /** Keyboard navigation enabled */
  enableKeyboardNavigation?: boolean;
  
  /** Currently focused row ID */
  focusedId?: string | null;
  
  /** Focus change callback */
  onFocusChange?: (id: string | null) => void;
  
  /** Container height */
  height?: number | string;
  
  /** Additional className */
  className?: string;
  
  /** Overscan count */
  overscan?: number;
  
  /** Column widths (persisted) */
  columnWidths?: Record<string, number>;
  
  /** Column width change callback */
  onColumnWidthChange?: (columnKey: string, width: number) => void;
  
  /** Stripe alternate rows */
  striped?: boolean;
}

// ============================================================================
// Column Resizer Component
// ============================================================================

interface ColumnResizerProps {
  columnKey: string;
  onResize: (columnKey: string, delta: number) => void;
  onResizeEnd: () => void;
}

function ColumnResizer({ columnKey, onResize, onResizeEnd }: ColumnResizerProps) {
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    startXRef.current = e.clientX;
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startXRef.current;
      startXRef.current = moveEvent.clientX;
      onResize(columnKey, delta);
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      onResizeEnd();
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [columnKey, onResize, onResizeEnd]);
  
  return (
    <div
      className={`absolute right-0 top-0 bottom-0 w-1 cursor-col-resize 
                  hover:bg-primary-400 active:bg-primary-500 
                  ${isResizing ? 'bg-primary-500' : 'bg-transparent'}`}
      onMouseDown={handleMouseDown}
      role="separator"
      aria-orientation="vertical"
    >
      <GripVertical className="h-3 w-3 absolute top-1/2 -translate-y-1/2 -translate-x-1/2 text-gray-400 opacity-0 hover:opacity-100" />
    </div>
  );
}

// ============================================================================
// Table Header Component
// ============================================================================

interface TableHeaderProps<T, SortField> {
  columns: VirtualizedTableColumn<T>[];
  columnWidths: Record<string, number>;
  selectable: boolean;
  allSelected: boolean;
  someSelected: boolean;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  currentSort?: SortState<SortField>;
  onSortChange?: (field: SortField, direction?: 'asc' | 'desc') => void;
  onColumnResize: (columnKey: string, delta: number) => void;
  onColumnResizeEnd: () => void;
  headerHeight: number;
}

function TableHeader<T, SortField = string>({
  columns,
  columnWidths,
  selectable,
  allSelected,
  someSelected,
  onSelectAll,
  onDeselectAll,
  currentSort,
  onSortChange,
  onColumnResize,
  onColumnResizeEnd,
  headerHeight,
}: TableHeaderProps<T, SortField>) {
  const handleHeaderClick = useCallback((column: VirtualizedTableColumn<T>) => {
    if (column.sortable && onSortChange) {
      const field = column.key as unknown as SortField;
      if (currentSort?.field === field) {
        // Toggle direction
        onSortChange(field, currentSort.direction === 'asc' ? 'desc' : 'asc');
      } else {
        // Set new sort
        onSortChange(field, 'asc');
      }
    }
  }, [currentSort, onSortChange]);
  
  return (
    <div
      className="flex bg-gray-50 border-b border-gray-200 sticky top-0 z-10"
      style={{ height: headerHeight }}
      role="row"
    >
      {/* Selection checkbox column */}
      {selectable && (
        <div
          className="flex items-center justify-center px-3 border-r border-gray-200 flex-shrink-0"
          style={{ width: 40 }}
          role="columnheader"
        >
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected && !allSelected;
            }}
            onChange={() => {
              if (allSelected || someSelected) {
                onDeselectAll();
              } else {
                onSelectAll();
              }
            }}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            aria-label="Select all rows"
          />
        </div>
      )}
      
      {/* Data columns */}
      {columns.map((column) => {
        const width = columnWidths[column.key] || (typeof column.width === 'number' ? column.width : 150);
        const isSorted = currentSort?.field === column.key;
        
        return (
          <div
            key={column.key}
            className={`relative flex items-center px-3 border-r border-gray-200 font-medium text-sm text-gray-700
                        ${column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''}`}
            style={{ 
              width,
              minWidth: column.minWidth || 50,
              maxWidth: column.maxWidth,
              textAlign: column.align || 'left',
            }}
            onClick={() => handleHeaderClick(column)}
            role="columnheader"
            aria-sort={isSorted ? (currentSort?.direction === 'asc' ? 'ascending' : 'descending') : undefined}
          >
            <span className="truncate flex-1">{column.label}</span>
            
            {/* Sort indicator */}
            {isSorted && (
              <span className="ml-1 flex-shrink-0">
                {currentSort?.direction === 'asc' ? (
                  <ArrowUp className="h-4 w-4 text-primary-600" />
                ) : (
                  <ArrowDown className="h-4 w-4 text-primary-600" />
                )}
              </span>
            )}
            
            {/* Resizer */}
            {column.resizable !== false && (
              <ColumnResizer
                columnKey={column.key}
                onResize={onColumnResize}
                onResizeEnd={onColumnResizeEnd}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// VirtualizedTable Component
// ============================================================================

export function VirtualizedTable<T, SortField = string>({
  items,
  getId,
  columns,
  rowHeight = 44,
  headerHeight = 40,
  selectedIds,
  onToggle,
  onSelectRange,
  onSelectAll,
  onDeselectAll,
  onRowClick,
  onRowDoubleClick,
  selectable = false,
  currentSort,
  onSortChange,
  enableKeyboardNavigation = true,
  focusedId,
  onFocusChange,
  height = '100%',
  className = '',
  overscan = 10,
  columnWidths: externalColumnWidths,
  onColumnWidthChange,
  striped = true,
}: VirtualizedTableProps<T, SortField>) {
  const parentRef = useRef<HTMLDivElement>(null);
  const lastSelectedIdRef = useRef<string | null>(null);
  
  // Column widths state (internal if not controlled)
  const [internalColumnWidths, setInternalColumnWidths] = useState<Record<string, number>>(() => {
    const widths: Record<string, number> = {};
    columns.forEach(col => {
      widths[col.key] = typeof col.width === 'number' ? col.width : 150;
    });
    return widths;
  });
  
  const columnWidths = externalColumnWidths || internalColumnWidths;
  
  // Calculate total width
  const totalWidth = useMemo(() => {
    const checkboxWidth = selectable ? 40 : 0;
    const columnsWidth = columns.reduce((sum, col) => sum + (columnWidths[col.key] || 150), 0);
    return checkboxWidth + columnsWidth;
  }, [columns, columnWidths, selectable]);
  
  // Virtualizer
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan,
  });
  
  const virtualItems = virtualizer.getVirtualItems();
  
  // Selection state
  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const someSelected = selectedIds.size > 0 && !allSelected;
  
  // Handle column resize
  const handleColumnResize = useCallback((columnKey: string, delta: number) => {
    const currentWidth = columnWidths[columnKey] || 150;
    const column = columns.find(c => c.key === columnKey);
    const minWidth = column?.minWidth || 50;
    const maxWidth = column?.maxWidth || 500;
    const newWidth = Math.min(maxWidth, Math.max(minWidth, currentWidth + delta));
    
    if (onColumnWidthChange) {
      onColumnWidthChange(columnKey, newWidth);
    } else {
      setInternalColumnWidths(prev => ({ ...prev, [columnKey]: newWidth }));
    }
  }, [columnWidths, columns, onColumnWidthChange]);
  
  const handleColumnResizeEnd = useCallback(() => {
    // Could persist column widths here
  }, []);
  
  // Handle row click
  const handleRowClick = useCallback((item: T, e: React.MouseEvent) => {
    const id = getId(item);
    
    // Update focus
    onFocusChange?.(id);
    
    // Handle selection
    if (selectable) {
      if (e.shiftKey && lastSelectedIdRef.current && onSelectRange) {
        onSelectRange(lastSelectedIdRef.current, id);
      } else if (e.ctrlKey || e.metaKey) {
        onToggle(id);
        lastSelectedIdRef.current = id;
      } else if (onRowClick) {
        onRowClick(item);
      } else {
        // Single click selects only this item
        onDeselectAll?.();
        onToggle(id);
        lastSelectedIdRef.current = id;
      }
    } else if (onRowClick) {
      onRowClick(item);
    }
  }, [getId, selectable, onToggle, onSelectRange, onDeselectAll, onRowClick, onFocusChange]);
  
  // Handle checkbox click
  const handleCheckboxClick = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (e.shiftKey && lastSelectedIdRef.current && onSelectRange) {
      onSelectRange(lastSelectedIdRef.current, id);
    } else {
      onToggle(id);
    }
    lastSelectedIdRef.current = id;
  }, [onToggle, onSelectRange]);
  
  // Keyboard navigation
  useEffect(() => {
    if (!enableKeyboardNavigation) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      const currentIndex = focusedId 
        ? items.findIndex(item => getId(item) === focusedId)
        : -1;
      
      switch (e.key) {
        case 'j':
        case 'ArrowDown': {
          e.preventDefault();
          const nextIndex = Math.min(currentIndex + 1, items.length - 1);
          if (nextIndex >= 0 && items[nextIndex]) {
            onFocusChange?.(getId(items[nextIndex]));
          }
          break;
        }
        case 'k':
        case 'ArrowUp': {
          e.preventDefault();
          const prevIndex = Math.max(currentIndex - 1, 0);
          if (prevIndex >= 0 && items[prevIndex]) {
            onFocusChange?.(getId(items[prevIndex]));
          }
          break;
        }
        case 'Enter':
        case 'o': {
          if (focusedId && onRowClick) {
            e.preventDefault();
            const item = items.find(i => getId(i) === focusedId);
            if (item) {
              onRowClick(item);
            }
          }
          break;
        }
        case ' ': {
          if (focusedId && selectable) {
            e.preventDefault();
            onToggle(focusedId);
            lastSelectedIdRef.current = focusedId;
          }
          break;
        }
        case 'Escape': {
          onDeselectAll?.();
          onFocusChange?.(null);
          break;
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enableKeyboardNavigation, items, getId, focusedId, onFocusChange, onRowClick, selectable, onToggle, onDeselectAll]);
  
  // Scroll focused row into view
  useEffect(() => {
    if (focusedId) {
      const index = items.findIndex(item => getId(item) === focusedId);
      if (index >= 0) {
        virtualizer.scrollToIndex(index, { align: 'auto' });
      }
    }
  }, [focusedId, items, getId, virtualizer]);
  
  return (
    <div
      ref={parentRef}
      className={`overflow-auto border border-gray-200 rounded-lg ${className}`}
      style={{ height }}
      role="grid"
      aria-rowcount={items.length}
      tabIndex={0}
    >
      {/* Header */}
      <TableHeader
        columns={columns}
        columnWidths={columnWidths}
        selectable={selectable}
        allSelected={allSelected}
        someSelected={someSelected}
        onSelectAll={onSelectAll || (() => {})}
        onDeselectAll={onDeselectAll || (() => {})}
        currentSort={currentSort}
        onSortChange={onSortChange}
        onColumnResize={handleColumnResize}
        onColumnResizeEnd={handleColumnResizeEnd}
        headerHeight={headerHeight}
      />
      
      {/* Body */}
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: totalWidth,
          position: 'relative',
        }}
      >
        {virtualItems.map((virtualItem) => {
          const item = items[virtualItem.index];
          const id = getId(item);
          const isSelected = selectedIds.has(id);
          const isFocused = focusedId === id;
          const isEven = virtualItem.index % 2 === 0;
          
          const itemProps: ItemRenderProps = {
            selected: isSelected,
            onSelect: () => onToggle(id),
            onToggle: () => onToggle(id),
            highlighted: isFocused,
          };
          
          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: rowHeight,
                transform: `translateY(${virtualItem.start}px)`,
              }}
              className={`flex border-b border-gray-100 transition-colors cursor-pointer
                          ${isFocused ? 'ring-2 ring-inset ring-primary-500' : ''}
                          ${isSelected ? 'bg-primary-50' : striped && isEven ? 'bg-white' : 'bg-gray-50/50'}
                          ${!isSelected && !isFocused ? 'hover:bg-gray-100' : ''}`}
              onClick={(e) => handleRowClick(item, e)}
              onDoubleClick={() => onRowDoubleClick?.(item)}
              role="row"
              aria-rowindex={virtualItem.index + 1}
              aria-selected={isSelected}
            >
              {/* Selection checkbox */}
              {selectable && (
                <div
                  className="flex items-center justify-center px-3 border-r border-gray-100 flex-shrink-0"
                  style={{ width: 40 }}
                  role="gridcell"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (e.nativeEvent.shiftKey && lastSelectedIdRef.current && onSelectRange) {
                        onSelectRange(lastSelectedIdRef.current, id);
                      } else {
                        onToggle(id);
                      }
                      lastSelectedIdRef.current = id;
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                    aria-label={`Select row ${virtualItem.index + 1}`}
                  />
                </div>
              )}
              
              {/* Data cells */}
              {columns.map((column) => {
                const width = columnWidths[column.key] || 150;
                
                return (
                  <div
                    key={column.key}
                    className="flex items-center px-3 border-r border-gray-100 overflow-hidden"
                    style={{ 
                      width,
                      minWidth: column.minWidth || 50,
                      textAlign: column.align || 'left',
                    }}
                    role="gridcell"
                  >
                    <div className="truncate w-full">
                      {column.render(item, itemProps)}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      
      {/* Empty state */}
      {items.length === 0 && (
        <div className="flex items-center justify-center h-32 text-gray-500">
          No items to display
        </div>
      )}
    </div>
  );
}

export default VirtualizedTable;
